import { readCacheScopeModelVersion } from '../../../shared/api/grpc/requestedParameterIntent';
import type { QuickFillSnapshotShapeOption } from '../../../shared/api/grpc/familyClient';

const DEFAULT_QUICK_FILL_CACHE_SCOPE = 'unknown-document';

const buildParameterSetKey = (parameterNames?: string[]): string =>
  JSON.stringify(
    [...new Set((parameterNames ?? []).map((name) => name.trim()).filter(Boolean))]
      .sort((left, right) => left.localeCompare(right))
  );

export const normalizeParameterSet = (parameterNames?: string[]): string[] =>
  JSON.parse(buildParameterSetKey(parameterNames)) as string[];

export const normalizeSnapshotShape = (
  snapshotShape?: QuickFillSnapshotShapeOption
): QuickFillSnapshotShapeOption =>
  snapshotShape === 'instanceOnly' ? 'instanceOnly' : 'familyType';

export const normalizeCacheScopeKey = (cacheScopeKey?: string | null): string => {
  const normalized = cacheScopeKey?.trim();
  return normalized ? normalized : DEFAULT_QUICK_FILL_CACHE_SCOPE;
};

export const hasExplicitModelVersionScope = (cacheScopeKey: string): boolean =>
  /::v\d+(?:::|$)/i.test(cacheScopeKey);

export const shouldUseQuickFillBrowserCache = (cacheScopeKey: string): boolean =>
  hasExplicitModelVersionScope(cacheScopeKey);

export const buildQuickFillDataCacheKey = (
  documentKey: string,
  parameterNames?: string[],
  snapshotShape?: QuickFillSnapshotShapeOption,
  dataScopeKey: string = 'scope:all'
): string =>
  `${normalizeCacheScopeKey(documentKey)}::${normalizeSnapshotShape(snapshotShape)}::${dataScopeKey}::${buildParameterSetKey(parameterNames)}`;

export const buildCacheKeyPrefix = (
  documentKey: string,
  snapshotShape?: QuickFillSnapshotShapeOption,
  dataScopeKey: string = 'scope:all'
): string =>
  `${normalizeCacheScopeKey(documentKey)}::${normalizeSnapshotShape(snapshotShape)}::${dataScopeKey}::`;

const removeModelVersion = (cacheKey: string): string =>
  cacheKey.replace(/::v(?:\d+|unknown)(?=::|$)/i, '');

const tryReadParameterSetFromCacheKey = (
  cacheKey: string,
  prefix: string
): string[] | null => {
  if (!cacheKey.startsWith(prefix)) {
    return null;
  }

  try {
    const parsed = JSON.parse(cacheKey.slice(prefix.length));
    if (!Array.isArray(parsed) || parsed.some((value) => typeof value !== 'string')) {
      return null;
    }

    return normalizeParameterSet(parsed);
  } catch {
    return null;
  }
};

const parameterSetSatisfiesRequest = (
  candidateParameterNames: string[],
  requestedParameterNames?: string[]
): boolean => {
  const requested = normalizeParameterSet(requestedParameterNames);
  if (requested.length === 0) {
    return true;
  }

  const candidate = new Set(candidateParameterNames);
  return requested.every((parameterName) => candidate.has(parameterName));
};

export const findCompatibleCacheKey = (
  candidateKeys: Array<{ cacheKey: string; storedAt?: number }>,
  documentKey: string,
  parameterNames: string[] | undefined,
  snapshotShape: QuickFillSnapshotShapeOption,
  dataScopeKey: string
): string | null => {
  const requestedVersion = readCacheScopeModelVersion(documentKey);
  const prefix = removeModelVersion(buildCacheKeyPrefix(documentKey, snapshotShape, dataScopeKey));
  const compatible = candidateKeys
    .map((candidate) => {
      const modelVersion = readCacheScopeModelVersion(candidate.cacheKey);
      return {
        ...candidate,
        modelVersion,
        parameterNames: tryReadParameterSetFromCacheKey(
          removeModelVersion(candidate.cacheKey),
          prefix
        ),
      };
    })
    .filter((candidate): candidate is {
      cacheKey: string;
      storedAt?: number;
      modelVersion: number | undefined;
      parameterNames: string[];
    } =>
      Array.isArray(candidate.parameterNames) &&
      (requestedVersion === undefined ||
        (candidate.modelVersion !== undefined && candidate.modelVersion >= requestedVersion)) &&
      parameterSetSatisfiesRequest(candidate.parameterNames, parameterNames)
    )
    .sort((left, right) =>
      left.parameterNames.length - right.parameterNames.length ||
      (right.modelVersion ?? -1) - (left.modelVersion ?? -1) ||
      (right.storedAt ?? 0) - (left.storedAt ?? 0)
    );

  return compatible[0]?.cacheKey ?? null;
};
